import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../util";

export interface TabItem<V extends string = string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  /** Count / status after the label. */
  badge?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps<V extends string = string> {
  items: ReadonlyArray<TabItem<V>>;
  value: V;
  onChange: (value: V) => void;
  /** Content of the active tab. Omit to render only the tab list. */
  children?: ReactNode;
  /** Accessible name for the tab list. */
  label?: string;
  className?: string;
  panelClassName?: string;
}

/**
 * Underline tabs (WAI-ARIA tabs pattern, automatic activation). Render the
 * active panel as `children`; switch on `value` yourself.
 */
export function Tabs<V extends string = string>({ items, value, onChange, children, label, className, panelClassName }: TabsProps<V>) {
  const base = useId().replace(/[^a-zA-Z0-9]/g, "");
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = items.filter((i) => !i.disabled);
  const tabId = (v: string) => `gf-tab-${base}-${v}`;
  const panelId = `gf-tabpanel-${base}`;

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key) || !enabled.length) return;
    e.preventDefault();
    const idx = Math.max(0, enabled.findIndex((i) => i.value === value));
    let next = idx;
    if (e.key === "ArrowRight") next = (idx + 1) % enabled.length;
    if (e.key === "ArrowLeft") next = (idx - 1 + enabled.length) % enabled.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = enabled.length - 1;
    const item = enabled[next];
    if (!item) return;
    onChange(item.value);
    refs.current[items.findIndex((i) => i.value === item.value)]?.focus();
  }

  return (
    <div className={cx("gf-tabs", className)}>
      <div role="tablist" aria-label={label} className="gf-tabs__list" onKeyDown={onKeyDown}>
        {items.map((item, i) => {
          const active = item.value === value;
          return (
            <button
              key={item.value}
              ref={(el) => {
                refs.current[i] = el;
              }}
              id={tabId(item.value)}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={children !== undefined && active ? panelId : undefined}
              tabIndex={active ? 0 : -1}
              disabled={item.disabled}
              className={cx("gf-tabs__tab", active && "gf-tabs__tab--active")}
              onClick={() => onChange(item.value)}
            >
              {item.icon ? <span className="gf-tabs__icon">{item.icon}</span> : null}
              <span>{item.label}</span>
              {item.badge !== undefined && item.badge !== null ? <span className="gf-tabs__badge">{item.badge}</span> : null}
            </button>
          );
        })}
      </div>
      {children !== undefined ? (
        <div role="tabpanel" id={panelId} aria-labelledby={tabId(value)} tabIndex={0} className={cx("gf-tabs__panel", panelClassName)}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
